import { UAParser } from 'ua-parser-js';
import { doc, getDoc, setDoc, arrayUnion } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface DeviceInfo {
  device: string;
  location: string;
  ip: string;
  time: string;
  fingerprint: string;
}

export function getDeviceFingerprint(): string {
  const parser = new UAParser();
  const result = parser.getResult();

  // Fingerprint must stay stable across routine browser/OS updates,
  // so version numbers are deliberately excluded
  const fingerprint = `${result.browser.name || 'unknown'}-${result.os.name || 'unknown'}-${result.device.type || 'desktop'}`;

  return fingerprint;
}

export async function getDeviceInfo(): Promise<Omit<DeviceInfo, 'fingerprint'>> {
  const parser = new UAParser();
  const result = parser.getResult();

  // Get device type and browser
  const deviceType = result.device.type || 'Desktop';
  const browser = result.browser.name || 'Unknown Browser';
  const os = result.os.name || 'Unknown OS';

  const device = `${deviceType} - ${browser} on ${os}`;

  // Try to get location and IP (using a public IP API)
  let location = 'Unknown Location';
  let ip = 'Unknown IP';

  try {
    const response = await fetch('https://ipapi.co/json/');
    if (response.ok) {
      const data = await response.json();
      location = `${data.city || 'Unknown'}, ${data.country_name || 'Unknown'}`;
      ip = data.ip || 'Unknown IP';
    }
  } catch (error) {
    // Failed to get location info
  }

  const time = new Date().toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });

  return { device, location, ip, time };
}

export function getStoredDevices(userId: string): string[] {
  if (typeof window === 'undefined') return [];

  const stored = localStorage.getItem(`devices_${userId}`);
  return stored ? JSON.parse(stored) : [];
}

export function storeDevice(userId: string, fingerprint: string): void {
  if (typeof window === 'undefined') return;

  const devices = getStoredDevices(userId);
  if (!devices.includes(fingerprint)) {
    devices.push(fingerprint);
    localStorage.setItem(`devices_${userId}`, JSON.stringify(devices));
  }
}

// Known devices live in Firestore (users/{uid}.knownDevices) so they survive
// cleared site data and work across tabs; localStorage is only a fast cache.
export async function isKnownDevice(userId: string, fingerprint: string): Promise<boolean> {
  const local = getStoredDevices(userId);
  if (local.includes(fingerprint)) return true;

  // Legacy fingerprints included version numbers (browser-ver-os-ver-type).
  // If this browser was already recorded under the old format, treat it as
  // known and silently upgrade the record instead of emailing.
  const browserName = fingerprint.split('-')[0];
  if (local.some((d) => d.split('-')[0] === browserName)) {
    await rememberDevice(userId, fingerprint);
    return true;
  }

  try {
    const snap = await getDoc(doc(db, 'users', userId));
    const known: string[] = (snap.exists() && snap.data().knownDevices) || [];
    if (known.includes(fingerprint)) {
      storeDevice(userId, fingerprint); // refresh local cache
      return true;
    }
  } catch (error) {
    // If Firestore is unreachable, err on the side of NOT emailing
    return true;
  }

  return false;
}

export async function rememberDevice(userId: string, fingerprint: string): Promise<void> {
  storeDevice(userId, fingerprint);
  try {
    await setDoc(doc(db, 'users', userId), { knownDevices: arrayUnion(fingerprint) }, { merge: true });
  } catch (error) {
    // Firestore write failed; localStorage cache still prevents repeat alerts here
  }
}
